import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  User,
  CheckCircle,
  Truck
} from 'lucide-react';

import {
  getCart,
  clearCart,
  imgUrl
} from '../data/mockData';

import axios from 'axios';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

import { toast } from 'sonner';


const Checkout = () => {

  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);

  const [isProcessing, setIsProcessing] = useState(false);

  const [orderComplete, setOrderComplete] = useState(false);


  const [formData, setFormData] = useState({
    // Customer Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    // Shipping Address
    address: '',
    city: '',
    postalCode: '',
    country: 'Shqipëri',

    // Additional Notes
    notes: ''
  });


  // ============================================================
  // LOAD CART
  // ============================================================

  useEffect(() => {

    const cart = getCart();

    if (cart.length === 0) {

      navigate('/cart');

      return;
    }

    setCartItems(cart);

  }, [navigate]);


  // ============================================================
  // FORM CHANGE
  // ============================================================

  const handleChange = (e) => {

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };


  // ============================================================
  // TOTALS
  // ============================================================

  const subtotal = cartItems.reduce(
    (total, item) =>
      total + (item.price * item.quantity),
    0
  );


  const shipping = 30000;


  const total =
    subtotal + shipping;


  // ============================================================
  // SUBMIT ORDER
  // ============================================================

  const handleSubmit = async (e) => {

    e.preventDefault();

    setIsProcessing(true);


    const orderData = {

      firstName:
        formData.firstName,

      lastName:
        formData.lastName,

      email:
        formData.email,

      phone:
        formData.phone,


      shippingAddress: {

        address:
          formData.address,

        city:
          formData.city,

        postalCode:
          formData.postalCode,

        country:
          formData.country
      },


      notes:
        formData.notes,


      items: cartItems.map(item => ({

        product_id:
          item.id,

        name:
          item.name,

        price:
          item.price,

        quantity:
          item.quantity,

        image:
          item.image,

        gender:
          item.gender || 'Të gjitha',

        season:
          item.season || 'Të gjitha',

        size:
          item.size || '',

        color:
          item.color || ''
      })),


      subtotal,

      shipping,

      total,

      paymentMethod:
        'cash_on_delivery'
    };


    try {

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders`,
        orderData
      );


      setOrderComplete(true);


      clearCart();


      window.dispatchEvent(
        new Event('cartUpdated')
      );


      setTimeout(() => {

        navigate('/');

        toast.success(
          'Porosia u vendos me sukses!'
        );

      }, 4000);


    } catch (err) {

      const detail =
        err.response?.data?.detail;


      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Ndodhi një gabim gjatë dërgimit të porosisë. Ju lutemi provoni përsëri.'
      );


    } finally {

      setIsProcessing(false);
    }
  };


  // ============================================================
  // ORDER COMPLETE
  // ============================================================

  if (orderComplete) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-center px-4 py-20 animate-in fade-in zoom-in duration-700">


          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-6">

            <CheckCircle className="h-12 w-12 text-green-600" />

          </div>


          <h2
            data-testid="order-complete-title"
            className="text-4xl font-serif font-bold text-gray-900 mb-4"
          >

            Porosia u Përfundua!

          </h2>


          <p className="text-lg text-gray-600 mb-2">

            Faleminderit për blerjen tuaj në Lor95 Boutique.

          </p>


          <p className="text-gray-500 mb-4">

            Ju kemi dërguar një email konfirmimi në{' '}
            {formData.email}

          </p>


          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 max-w-md mx-auto">

            <div className="flex items-center justify-center space-x-2 text-blue-700">

              <Truck className="h-5 w-5" />

              <p className="font-semibold">
                Pagesa në Dorëzim
              </p>

            </div>


            <p className="text-sm text-blue-600 mt-2">

              Do të paguani kur të merrni produktet

            </p>

          </div>


          <p className="text-sm text-gray-500">

            Duke ju ridrejtuar në faqen kryesore...

          </p>

        </div>

      </div>
    );
  }


  // ============================================================
  // RENDER
  // ============================================================

  return (

    <div className="min-h-screen bg-gray-50 pt-24 pb-20">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">


        {/* ======================================================
            PAGE HEADER
        ======================================================= */}

        <div className="mb-8 animate-in fade-in slide-in-from-top duration-700">

          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-gray-900 mb-2">

            Përfundimi i Porosisë

          </h1>


          <p className="text-gray-600">

            Plotësoni të dhënat tuaja për dorëzim

          </p>

        </div>


        <form onSubmit={handleSubmit}>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">


            {/* ==================================================
                LEFT SIDE
            =================================================== */}

            <div className="lg:col-span-2 space-y-6">


              {/* ================================================
                  CUSTOMER INFORMATION
              ================================================= */}

              <Card className="border-none shadow-md animate-in fade-in slide-in-from-left duration-700">

                <CardContent className="p-8">

                  <div className="flex items-center space-x-3 mb-6">

                    <User className="h-6 w-6 text-gray-900" />

                    <h2 className="text-2xl font-semibold text-gray-900">

                      Informacioni i Klientit

                    </h2>

                  </div>


                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">


                    <div>

                      <Label htmlFor="firstName">
                        Emri *
                      </Label>

                      <Input
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                      />

                    </div>


                    <div>

                      <Label htmlFor="lastName">
                        Mbiemri *
                      </Label>

                      <Input
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                      />

                    </div>


                    <div>

                      <Label htmlFor="email">
                        Email *
                      </Label>

                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                      />

                    </div>


                    <div>

                      <Label htmlFor="phone">
                        Telefoni *
                      </Label>

                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                        placeholder="+355 69 123 4567"
                      />

                    </div>

                  </div>

                </CardContent>

              </Card>


              {/* ================================================
                  SHIPPING ADDRESS
              ================================================= */}

              <Card className="border-none shadow-md animate-in fade-in slide-in-from-left duration-700 delay-100">

                <CardContent className="p-8">

                  <div className="flex items-center space-x-3 mb-6">

                    <MapPin className="h-6 w-6 text-gray-900" />

                    <h2 className="text-2xl font-semibold text-gray-900">

                      Adresa e Dorëzimit

                    </h2>

                  </div>


                  <div className="space-y-4">


                    <div>

                      <Label htmlFor="address">
                        Adresa *
                      </Label>

                      <Input
                        id="address"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                        placeholder="Rruga, Numri"
                      />

                    </div>


                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">


                      <div>

                        <Label htmlFor="city">
                          Qyteti *
                        </Label>

                        <Input
                          id="city"
                          name="city"
                          required
                          value={formData.city}
                          onChange={handleChange}
                          className="mt-2 rounded-none"
                          placeholder=""
                        />

                      </div>


                      <div>

                        <Label htmlFor="postalCode">
                          Kodi Postar *
                        </Label>

                        <Input
                          id="postalCode"
                          name="postalCode"
                          required
                          value={formData.postalCode}
                          onChange={handleChange}
                          className="mt-2 rounded-none"
                          placeholder=""
                        />

                      </div>


                      <div>

                        <Label htmlFor="country">
                          Shteti *
                        </Label>

                        <Input
                          id="country"
                          name="country"
                          required
                          value={formData.country}
                          onChange={handleChange}
                          className="mt-2 rounded-none"
                        />

                      </div>

                    </div>


                    <div>

                      <Label htmlFor="notes">

                        Shënime Shtesë (Opsionale)

                      </Label>

                      <Input
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="mt-2 rounded-none"
                        placeholder="Instruksione të veçanta për dorëzim..."
                      />

                    </div>

                  </div>

                </CardContent>

              </Card>


              {/* ================================================
                  PAYMENT METHOD
              ================================================= */}

              <Card className="border-none shadow-md animate-in fade-in slide-in-from-left duration-700 delay-200">

                <CardContent className="p-8">

                  <div className="flex items-center space-x-3 mb-6">

                    <Truck className="h-6 w-6 text-gray-900" />

                    <h2 className="text-2xl font-semibold text-gray-900">

                      Metoda e Pagesës

                    </h2>

                  </div>


                  <div className="bg-gray-50 border-2 border-gray-900 rounded-lg p-6">


                    <div className="flex items-center justify-between mb-4">

                      <div className="flex items-center space-x-3">

                        <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">

                          <div className="w-3 h-3 rounded-full bg-white"></div>

                        </div>


                        <h3 className="text-lg font-semibold text-gray-900">

                          Pagesa në Dorëzim (Cash)

                        </h3>

                      </div>


                      <Truck className="h-8 w-8 text-gray-700" />

                    </div>


                    <p className="text-gray-600 leading-relaxed">

                      Paguani me para në dorë kur të merrni produktet.
                      Kurieri ynë do t'ju kontaktojë për të konfirmuar
                      dorëzimin dhe adresën tuaj.

                    </p>


                    <div className="mt-4 pt-4 border-t border-gray-200">

                      <ul className="space-y-2 text-sm text-gray-600">


                        <li className="flex items-center space-x-2">

                          <span className="text-green-600">
                            ✓
                          </span>

                          <span>
                            Pa tarifa shtesë
                          </span>

                        </li>


                        <li className="flex items-center space-x-2">

                          <span className="text-green-600">
                            ✓
                          </span>

                          <span>
                            Pagesë e sigurt në dorëzim
                          </span>

                        </li>


                        <li className="flex items-center space-x-2">

                          <span className="text-green-600">
                            ✓
                          </span>

                          <span>
                            Mundësi kthimi nëse produkti nuk është i përshtatshëm
                          </span>

                        </li>

                      </ul>

                    </div>

                  </div>

                </CardContent>

              </Card>

            </div>


            {/* ==================================================
                ORDER SUMMARY
            =================================================== */}

            <div className="lg:col-span-1">

              <Card className="border-none shadow-xl sticky top-24 animate-in fade-in slide-in-from-right duration-700">

                <CardContent className="p-8">


                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">

                    Përmbledhja e Porosisë

                  </h2>


                  {/* ============================================
                      PRODUCTS
                  ============================================= */}

                  <div className="space-y-4 mb-6">

                    {cartItems.map((item) => (

                      <div
                        key={item.cartId || item.id}
                        className="flex items-center space-x-4"
                      >


                        <img
                          src={imgUrl(item.image)}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />


                        <div className="flex-grow min-w-0">


                          <p className="text-sm font-medium text-gray-900 line-clamp-1">

                            {item.name}

                          </p>


                          <p className="text-xs text-gray-500 mt-1">

                            Sasia: {item.quantity}

                            {item.color && (

                              <>
                                {' • '}
                                Ngjyra: {item.color}
                              </>

                            )}

                            {item.size && (

                              <>
                                {' • '}
                                Masa: {item.size}
                              </>

                            )}

                          </p>

                        </div>


                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">

                          {
                            (
                              (
                                item.price
                                *
                                item.quantity
                              )
                              /
                              100
                            ).toFixed(0)
                          }
                          {' '}
                          Lek

                        </p>

                      </div>

                    ))}

                  </div>


                  <Separator className="my-6" />


                  {/* ============================================
                      TOTALS
                  ============================================= */}

                  <div className="space-y-3">


                    <div className="flex justify-between text-gray-600">

                      <span>
                        Nëntotali
                      </span>


                      <span className="font-medium">

                        {
                          (
                            subtotal
                            /
                            100
                          ).toFixed(0)
                        }
                        {' '}
                        Lek

                      </span>

                    </div>


                    <div className="flex justify-between text-gray-600">

                      <span>
                        Transporti
                      </span>


                      <span className="font-medium">

                        {
                          shipping === 0
                            ? 'Falas'
                            : `${
                                (
                                  shipping
                                  /
                                  100
                                ).toFixed(0)
                              } Lek`
                        }

                      </span>

                    </div>


                    <Separator />


                    <div className="flex justify-between text-gray-900">

                      <span className="text-lg font-semibold">

                        Totali

                      </span>


                      <span className="text-2xl font-bold">

                        {
                          (
                            total
                            /
                            100
                          ).toFixed(0)
                        }
                        {' '}
                        Lek

                      </span>

                    </div>


                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">

                      <div className="flex items-center space-x-2 text-blue-700">

                        <Truck className="h-4 w-4" />

                        <span className="text-sm font-medium">

                          Pagesa në Dorëzim

                        </span>

                      </div>

                    </div>

                  </div>


                  {/* ============================================
                      SUBMIT BUTTON
                  ============================================= */}

                  <Button
                    type="submit"
                    data-testid="checkout-submit-btn"
                    disabled={isProcessing}
                    size="lg"
                    className="w-full mt-8 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-none py-6 transition-all duration-300 hover:scale-105"
                  >

                    {
                      isProcessing
                        ? 'Duke Procesuar...'
                        : (
                          <>

                            <Truck className="mr-2 h-5 w-5" />

                            Konfirmo Porosinë

                          </>
                        )
                    }

                  </Button>


                  <p className="text-xs text-gray-500 text-center mt-4">

                    Duke klikuar "Konfirmo Porosinë",
                    ju pranoni kushtet tona të shërbimit

                  </p>

                </CardContent>

              </Card>

            </div>

          </div>

        </form>

      </div>

    </div>
  );
};


export default Checkout;